import { useState, useRef, useEffect, useCallback } from "react";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Download, Move, RotateCw } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";

type FrameType = "browser" | "phone" | "tablet";

const FRAMES: { id: FrameType; label: string }[] = [
  { id: "browser", label: "Browser Window" },
  { id: "phone", label: "Phone" },
  { id: "tablet", label: "Tablet" },
];

const TOOL_INFO_CONTENT = {
  what: `Places your design or image into a realistic device or product mockup (a browser window, a phone or a tablet frame), useful for presenting work without needing separate mockup software.`,
  how: `Upload your design, choose a mockup template, and the tool composites them together automatically. For the Phone and Tablet frames, drag the image to reposition it and use the zoom slider to adjust how much is visible before downloading.`,
  tip: `The Phone and Tablet frames crop your image to fill the screen area (since real device screens have a fixed shape) — drag to choose which part of your image stays in frame rather than accepting wherever it lands by default. The Browser frame stretches to fit exactly, so no cropping or repositioning applies there.`,
};

export default function MockupGenerator() {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);
  const [frame, setFrame] = useState<FrameType>("browser");
  // Pan offset (in the image's own natural-pixel space, at the base
  // "cover" scale) and extra zoom on top of the base cover-fit scale.
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [zoom, setZoom] = useState([1]);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
  // "Fit" shows the whole image inside the screen (letterboxed) instead of
  // cropping it to fill.
  const [fit, setFit] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragStart = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setPanX(0); setPanY(0); setZoom([1]); setRotation(0);
    const img = new Image();
    img.onload = () => setImgEl(img);
    img.src = url;
  }, []);

  const resetAdjust = () => { setPanX(0); setPanY(0); setZoom([1]); };

  // Rotates the source image onto an offscreen canvas *before* any of the
  // existing frame-compositing logic runs, so every frame's math (which
  // assumes a plain upright image) works completely unchanged -- rotation
  // becomes a pre-processing step, not a special case each frame has to
  // handle separately.
  const getRotatedSource = useCallback((): HTMLCanvasElement | HTMLImageElement | null => {
    if (!imgEl) return null;
    if (rotation === 0) return imgEl;
    const swapped = rotation === 90 || rotation === 270;
    const w = swapped ? imgEl.naturalHeight : imgEl.naturalWidth;
    const h = swapped ? imgEl.naturalWidth : imgEl.naturalHeight;
    const off = document.createElement("canvas");
    off.width = w; off.height = h;
    const octx = off.getContext("2d");
    if (!octx) return imgEl;
    octx.translate(w / 2, h / 2);
    octx.rotate((rotation * Math.PI) / 180);
    octx.drawImage(imgEl, -imgEl.naturalWidth / 2, -imgEl.naturalHeight / 2);
    return off;
  }, [imgEl, rotation]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const source = getRotatedSource();
    if (!canvas || !source || !imgEl) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const sourceWidth = source instanceof HTMLCanvasElement ? source.width : source.naturalWidth;
    const sourceHeight = source instanceof HTMLCanvasElement ? source.height : source.naturalHeight;

    // Draws the image "cover" fit into (screenX, screenY, screenW, screenH),
    // adjusted by the current pan offset and zoom, and clamped so the
    // image can never be dragged to reveal empty space at the edges.
    const drawCovered = (screenX: number, screenY: number, screenW: number, screenH: number) => {
      const baseScale = (fit ? Math.min : Math.max)(screenW / sourceWidth, screenH / sourceHeight);
      if (fit) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(screenX, screenY, screenW, screenH);
      }
      const scale = baseScale * zoom[0];
      const dw = sourceWidth * scale, dh = sourceHeight * scale;
      const maxOffsetX = Math.max(0, (dw - screenW) / 2);
      const maxOffsetY = Math.max(0, (dh - screenH) / 2);
      const clampedPanX = Math.max(-maxOffsetX, Math.min(maxOffsetX, panX));
      const clampedPanY = Math.max(-maxOffsetY, Math.min(maxOffsetY, panY));
      ctx.save();
      ctx.beginPath();
      ctx.rect(screenX, screenY, screenW, screenH);
      ctx.clip();
      ctx.drawImage(
        source,
        screenX + (screenW - dw) / 2 + clampedPanX,
        screenY + (screenH - dh) / 2 + clampedPanY,
        dw, dh
      );
      ctx.restore();
    };

    if (frame === "browser") {
      const w = 1200, barH = 44;
      const h = Math.round((sourceHeight / sourceWidth) * w) + barH;
      canvas.width = w;
      canvas.height = h;
      ctx.fillStyle = "#e5e7eb";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "#f3f4f6";
      ctx.fillRect(0, 0, w, barH);
      ["#ef4444", "#f59e0b", "#22c55e"].forEach((c, i) => {
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(24 + i * 22, barH / 2, 7, 0, Math.PI * 2);
        ctx.fill();
      });
      // Browser frame stretches to fit exactly -- no cropping happens
      // here, so pan/zoom adjustment doesn't apply to this frame.
      ctx.drawImage(source, 0, barH, w, h - barH);
    } else if (frame === "phone") {
      const frameW = 600, frameH = 1220, radius = 60, bezel = 24, notchW = 160, notchH = 30;
      canvas.width = frameW;
      canvas.height = frameH;
      ctx.fillStyle = "#111827";
      ctx.beginPath();
      ctx.roundRect(0, 0, frameW, frameH, radius);
      ctx.fill();

      const screenX = bezel, screenY = bezel, screenW = frameW - bezel * 2, screenH = frameH - bezel * 2;
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(screenX, screenY, screenW, screenH, radius - 12);
      ctx.clip();
      drawCovered(screenX, screenY, screenW, screenH);
      ctx.restore();

      ctx.fillStyle = "#111827";
      ctx.beginPath();
      ctx.roundRect((frameW - notchW) / 2, bezel, notchW, notchH, 16);
      ctx.fill();
    } else {
      const frameW = 900, frameH = 1200, radius = 40, bezel = 40;
      canvas.width = frameW;
      canvas.height = frameH;
      ctx.fillStyle = "#1f2937";
      ctx.beginPath();
      ctx.roundRect(0, 0, frameW, frameH, radius);
      ctx.fill();

      const screenX = bezel, screenY = bezel, screenW = frameW - bezel * 2, screenH = frameH - bezel * 2;
      drawCovered(screenX, screenY, screenW, screenH);
    }
  }, [imgEl, frame, panX, panY, zoom, fit, getRotatedSource]);

  useEffect(() => {
    draw();
  }, [draw]);

  const canAdjust = frame === "phone" || frame === "tablet";

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!canAdjust) return;
    dragStart.current = { x: e.clientX, y: e.clientY, panX, panY };
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragStart.current || !canvasRef.current) return;
    // Scale mouse-movement pixels to canvas-space pixels, since the
    // canvas is displayed smaller than its actual internal resolution.
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleFactor = canvasRef.current.width / rect.width;
    const dx = (e.clientX - dragStart.current.x) * scaleFactor;
    const dy = (e.clientY - dragStart.current.y) * scaleFactor;
    setPanX(dragStart.current.panX + dx);
    setPanY(dragStart.current.panY + dy);
  };
  const onPointerUp = () => { dragStart.current = null; };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(blob => {
      if (blob) downloadBlob(blob, `mockup-${frame}.png`);
    }, "image/png");
  };

  return (
    <ToolContainer>
      {!imageUrl ? (
        <FileDropZone gateAtDownload
          accept="image/*"
          onFiles={handleFiles}
          label="Drop a screenshot or design to place in a device frame"
          description="Processed entirely in your browser"
        />
      ) : (
        <div className="space-y-5">
          <div>
            <Label>Frame</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {FRAMES.map(f => (
                <Button key={f.id} size="sm" variant={frame === f.id ? "default" : "outline"} onClick={() => { setFrame(f.id); resetAdjust(); }}>
                  {f.label}
                </Button>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => { setRotation(r => (r + 90) % 360); resetAdjust(); }}
                title="Rotate 90°"
              >
                <RotateCw className="h-3.5 w-3.5" /> Rotate ({rotation}°)
              </Button>
            </div>
          </div>

          <div className="flex justify-center bg-muted/30 rounded-xl p-6">
            <canvas
              ref={canvasRef}
              className={`max-w-full max-h-[500px] object-contain ${canAdjust ? "cursor-move" : ""}`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              style={{ touchAction: "none" }}
            />
          </div>

          {canAdjust && (
            <div className="space-y-2 max-w-sm mx-auto">
              <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
                <Move className="h-3.5 w-3.5" /> Drag the image above to reposition it
              </div>
              <Label className="text-xs">Zoom: {zoom[0].toFixed(1)}x</Label>
              <Slider value={zoom} onValueChange={setZoom} min={1} max={2.5} step={0.1} />
              <div className="text-center flex flex-wrap justify-center gap-2">
                <Button variant={fit ? "default" : "outline"} size="sm" onClick={() => { setFit(f => !f); resetAdjust(); }}>
                  {fit ? "Fit: whole image" : "Fit whole image"}
                </Button>
                <Button variant="ghost" size="sm" onClick={resetAdjust}>Reset Position</Button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Button onClick={handleDownload} className="flex-1 gap-2">
              <Download className="h-4 w-4" /> Download Mockup (PNG)
            </Button>
            <Button variant="outline" onClick={() => { setImageUrl(null); setImgEl(null); }}>
              Upload Different Image
            </Button>
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
